import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Card as MuiCard,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SaveIcon from '@material-ui/icons/Save';
import { Toast } from 'components/shared/Toaster';
import { updateFeatureFlags } from 'actions/user';
import { getUserDetailByToken } from 'actions/root';
import { getMarketplaceCriteria, createMarketplaceCriteria, updateMarketplaceCriteria } from 'actions/user';
import MarketplaceCriteriaForm from 'components/RoxAdmin/DialogForms/MarketplaceCriteriaForm';
import FMCSACriteriaForm from 'components/RoxAdmin/DialogForms/FMCSACriteriaForm';
import ControlledSOSCriteriaForm from 'components/RoxAdmin/DialogForms/ControlledSOSCriteriaForm';
import ControlledUCCCriteriaForm from 'components/RoxAdmin/DialogForms/ControlledUCCCriteriaForm';
import ControlledBackgroundCriteriaForm from 'components/RoxAdmin/DialogForms/ControlledBackgroundCriteriaForm';
import strings from 'strings';


const FeatureFlagsForm = () => {
  const dispatch = useDispatch();
  const client = useSelector(state => state.user.userDetails.securityContext.client);
  
  const [featureFlags, setFeatureFlags] = useState({
    optInToBuyNewApplications: false,
    optOutOfSharingDeclinedApplications: false
  });
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    flagName: '',
    newValue: false,
    title: '',
    message: ''
  });

  const [marketplaceCriteria, setMarketplaceCriteria] = useState({
    isActive: false,
    marketPlacePolicy: {
      generalPolicy: {
        excludedStates: [],
        excludedZipCodes: [],
        excludedIndustryTypes: [],
        excludedBusinessTypes: [],
        longevityPolicy: {
          enabled: false,
          minimumYearsInBusiness: null
        },
        requiredBusinessEmail: false,
        requiredBusinessPhone: false
      },
      fmcsaPolicy: {
        enabled: false,
        minPowerUnits: null,
        maxPowerUnits: null,
        minDrivers: null,
        maxDriverOosRate: null,
        minimumDriverInspections: null,
        maxVehicleOosRate: null,
        minimumVehicleInspections: null,
        maxHazmatOosRate: null,
        minimumHazmatInspections: null,
        requireAllowedToOperate: false
      },
      sosPolicy: {
        enabled: false,
        excludeInactives: false,
        excludeUnknown: false
      },
      uccPolicy: {
        enabled: false,
        maxActiveLiens: null,
        excludedSecuredParties: []
      },
      backgroundPolicy: {
        enabled: false,
        excludedCriminalRecordCaseTypes: [],
        excludedCriminalRecordDispositions: [],
        requiredCleanCriminalRecord: false,
        criminalRecordsLookBackYears: null,
        requiredCleanLiensAndJudgements: false,
        excludedLiensAndJudgmentFilingTypes: [],
        liensAndJudgmentFilingMaxAmount: null,
        requiredCleanBankruptcies: false,
        excludedBankruptciesChapters: [],
        excludedBankruptciesDispositions: [],
        bankruptciesLookBackYears: null
      }
    }
  });
  const [existingCriteria, setExistingCriteria] = useState(false);
  const [savingCriteria, setSavingCriteria] = useState(false);

  const fetchMarketplaceCriteria = async () => {
    try {
      const res = await getMarketplaceCriteria();
      if (res?.response) {
        const data = res.response;
        setMarketplaceCriteria({
          isActive: data.active || false,
          marketPlacePolicy: {
            generalPolicy: data.marketPlacePolicy?.generalPolicy || marketplaceCriteria.marketPlacePolicy.generalPolicy,
            fmcsaPolicy: data.marketPlacePolicy?.fmcsaPolicy || marketplaceCriteria.marketPlacePolicy.fmcsaPolicy,
            sosPolicy: data.marketPlacePolicy?.sosPolicy || marketplaceCriteria.marketPlacePolicy.sosPolicy,
            uccPolicy: data.marketPlacePolicy?.uccPolicy || marketplaceCriteria.marketPlacePolicy.uccPolicy,
            backgroundPolicy: data.marketPlacePolicy?.backgroundPolicy || marketplaceCriteria.marketPlacePolicy.backgroundPolicy,
          }
        });
        setExistingCriteria(true);
      }
    } catch (error) {
      console.error('Error fetching marketplace criteria:', error);
    }
  };

  const fetchUserDetails = async () => {
    try {
      setInitialLoading(true);
      const userDetails = await dispatch(getUserDetailByToken());
      const clientData = userDetails?.securityContext?.client;
      
      const initialFlags = {
        optInToBuyNewApplications: clientData?.readingApplicationsOffersEnabled || false,
        optOutOfSharingDeclinedApplications: clientData?.decliningApplicationsOffersEnabled || false
      };
      
      setFeatureFlags(initialFlags);
      await fetchMarketplaceCriteria();
    } catch (error) {
      if (client) {
        const fallbackFlags = {
          optInToBuyNewApplications: client?.readingApplicationsOffersEnabled || false,
          optOutOfSharingDeclinedApplications: client?.decliningApplicationsOffersEnabled || false
        };
        setFeatureFlags(fallbackFlags);
      }
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [dispatch]); 

  const updateMarketplaceCriteriaField = (field, value) => {
    setMarketplaceCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateMarketplacePolicyField = (policyType, field, value) => {
    setMarketplaceCriteria(prev => ({
      ...prev,
      marketPlacePolicy: {
        ...prev.marketPlacePolicy,
        [policyType]: {
          ...prev.marketPlacePolicy[policyType],
          [field]: value
        }
      }
    }));
  };

  const handleSaveMarketplaceCriteria = async () => {
    setSavingCriteria(true);
    try {
      const payload = {
        active: marketplaceCriteria.isActive,
        marketPlacePolicy: marketplaceCriteria.marketPlacePolicy
      };

      const action = existingCriteria ? updateMarketplaceCriteria : createMarketplaceCriteria;
      const result = await action(payload);

      if (result) {
        Toast({
          type: 'success',
          message: strings.MARKETPLACE.SUCCESS_MESSAGE
        });
        setExistingCriteria(true);
      } else {
        Toast({
          type: 'error', 
          message: strings.MARKETPLACE.ERROR_MESSAGE
        });
      }
    } catch (error) {
      console.error('Error saving marketplace criteria:', error);
      Toast({
        type: 'error',
        message: strings.MARKETPLACE.FAILED_MESSAGE
      });
    } finally {
      setSavingCriteria(false);
    }
  }; 

  const handleFeatureFlagChange = (flagName, value) => {
    const getConfirmationContent = (flagName, value) => {
      if (flagName === 'optInToBuyNewApplications') {
        return {
          title: value ? strings.MARKETPLACE.CONFIRMATION_ENABLE_BUYING_TITLE : strings.MARKETPLACE.CONFIRMATION_DISABLE_BUYING_TITLE,
          message: value 
            ? strings.MARKETPLACE.CONFIRMATION_ENABLE_BUYING_MESSAGE
            : strings.MARKETPLACE.CONFIRMATION_DISABLE_BUYING_MESSAGE
        };
      } else if (flagName === 'optOutOfSharingDeclinedApplications') {
        return {
          title: value ? strings.MARKETPLACE.CONFIRMATION_STOP_SHARING_TITLE : strings.MARKETPLACE.CONFIRMATION_ENABLE_SHARING_TITLE,
          message: value 
            ? strings.MARKETPLACE.CONFIRMATION_STOP_SHARING_MESSAGE
            : strings.MARKETPLACE.CONFIRMATION_ENABLE_SHARING_MESSAGE
        };
      }
      return { title: strings.CONFIRM, message: 'Are you sure you want to make this change?' };
    };

    const { title, message } = getConfirmationContent(flagName, value);
    
    setConfirmDialog({
      open: true,
      flagName,
      newValue: value,
      title,
      message
    });
  };

  const handleConfirmChange = async () => {
    setFeatureFlagsLoading(true);
    
    try {
      const updatedFlags = { 
        ...featureFlags, 
        [confirmDialog.flagName]: confirmDialog.newValue 
      };
      
      const payload = {
        marketplaceBuyer: updatedFlags.optInToBuyNewApplications,
        marketplaceProvider: updatedFlags.optOutOfSharingDeclinedApplications
      };
      
      await updateFeatureFlags(payload)();
      
      setFeatureFlags(updatedFlags);
      Toast({ type: 'success', message: strings.MARKETPLACE.FLAG_SUCCESS });
      
      await fetchUserDetails();
    } catch (error) {
      console.error('Error updating feature flag:', error);
      Toast({ type: 'error', message: strings.MARKETPLACE.FLAG_ERROR });
    } finally {
      setFeatureFlagsLoading(false);
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const handleCancelChange = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const FeatureToggleRow = ({ title, description, checked, onChange, disabled }) => (
    <Box style={{ paddingTop: 16, paddingBottom: 16 }}>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <Box style={{ flex: 1 }}>
          <Typography variant="subtitle1" component="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              color="primary"
            />
          }
          label=""
          style={{ margin: 0 }}
        />
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" style={{ paddingBottom: 24 }}>
      <MuiCard style={{ padding: 24 }}>
        {initialLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={300}
          >
            <CircularProgress size={48} />
          </Box>
        ) : (
          <>
            <Box style={{ marginBottom: 24 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {strings.MARKETPLACE.TITLE}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {strings.MARKETPLACE.ADMIN_ONLY}
              </Typography>
            </Box>

        <Box style={{ paddingTop: 16, paddingBottom: 16 }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <Box style={{ flex: 1 }}>
              <Typography variant="subtitle1" component="h6" gutterBottom>
                {strings.MARKETPLACE.OPT_IN_TITLE}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {strings.MARKETPLACE.OPT_IN_DESCRIPTION}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={featureFlags.optInToBuyNewApplications}
                  onChange={(e) => handleFeatureFlagChange('optInToBuyNewApplications', e.target.checked)}
                  disabled={featureFlagsLoading}
                  color="primary"
                />
              }
              label=""
              style={{ margin: 0 }}
            />
          </Box>
          
          {featureFlags.optInToBuyNewApplications && (
            <Box style={{ marginTop: 16 }}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {strings.MARKETPLACE.GENERAL_CRITERIA}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box style={{ width: '100%' }}>
                    <MarketplaceCriteriaForm 
                      value={marketplaceCriteria.marketPlacePolicy.generalPolicy}
                      isActive={marketplaceCriteria.isActive}
                      onUpdateActive={(value) => updateMarketplaceCriteriaField('isActive', value)}
                      onUpdatePolicy={(field, value) => updateMarketplacePolicyField('generalPolicy', field, value)}
                      hideGlobalControls={false}
                      hideSubmitButton={true}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              <Accordion style={{ marginTop: 8 }} expanded={marketplaceCriteria.marketPlacePolicy.fmcsaPolicy.enabled}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 16 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {strings.MARKETPLACE.FMCSA_CRITERIA}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={marketplaceCriteria.marketPlacePolicy.fmcsaPolicy.enabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateMarketplacePolicyField('fmcsaPolicy', 'enabled', e.target.checked);
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={strings.MARKETPLACE.ENABLE}
                      style={{ margin: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </AccordionSummary>
                {marketplaceCriteria.marketPlacePolicy.fmcsaPolicy.enabled && (
                  <AccordionDetails>
                    <Box style={{ width: '100%' }}>
                      <FMCSACriteriaForm 
                        value={marketplaceCriteria.marketPlacePolicy.fmcsaPolicy}
                        onUpdatePolicy={(field, value) => updateMarketplacePolicyField('fmcsaPolicy', field, value)}
                        hideSubmitButton={true}
                      />
                    </Box>
                  </AccordionDetails>
                )}
              </Accordion>
              
              <Accordion style={{ marginTop: 8 }} expanded={marketplaceCriteria.marketPlacePolicy.sosPolicy.enabled}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 16 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {strings.MARKETPLACE.SOS_CRITERIA}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={marketplaceCriteria.marketPlacePolicy.sosPolicy.enabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateMarketplacePolicyField('sosPolicy', 'enabled', e.target.checked);
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={strings.MARKETPLACE.ENABLE}
                      style={{ margin: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </AccordionSummary>
                {marketplaceCriteria.marketPlacePolicy.sosPolicy.enabled && (
                  <AccordionDetails>
                    <Box style={{ width: '100%' }}>
                      <ControlledSOSCriteriaForm 
                        value={marketplaceCriteria.marketPlacePolicy.sosPolicy}
                        onUpdatePolicy={(field, value) => updateMarketplacePolicyField('sosPolicy', field, value)}
                      />
                    </Box>
                  </AccordionDetails>
                )}
              </Accordion>
              
              <Accordion style={{ marginTop: 8 }} expanded={marketplaceCriteria.marketPlacePolicy.uccPolicy.enabled}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 16 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {strings.MARKETPLACE.UCC_CRITERIA}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={marketplaceCriteria.marketPlacePolicy.uccPolicy.enabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateMarketplacePolicyField('uccPolicy', 'enabled', e.target.checked);
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={strings.MARKETPLACE.ENABLE}
                      style={{ margin: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </AccordionSummary>
                {marketplaceCriteria.marketPlacePolicy.uccPolicy.enabled && (
                  <AccordionDetails>
                    <Box style={{ width: '100%' }}>
                      <ControlledUCCCriteriaForm 
                        value={marketplaceCriteria.marketPlacePolicy.uccPolicy}
                        onUpdatePolicy={(field, value) => updateMarketplacePolicyField('uccPolicy', field, value)}
                      />
                    </Box>
                  </AccordionDetails>
                )}
              </Accordion>
              
              <Accordion style={{ marginTop: 8 }} expanded={marketplaceCriteria.marketPlacePolicy.backgroundPolicy.enabled}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 16 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {strings.MARKETPLACE.BACKGROUND_CRITERIA}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={marketplaceCriteria.marketPlacePolicy.backgroundPolicy.enabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateMarketplacePolicyField('backgroundPolicy', 'enabled', e.target.checked);
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={strings.MARKETPLACE.ENABLE}
                      style={{ margin: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </AccordionSummary>
                {marketplaceCriteria.marketPlacePolicy.backgroundPolicy.enabled && (
                  <AccordionDetails>
                    <Box style={{ width: '100%' }}>
                      <ControlledBackgroundCriteriaForm 
                        value={marketplaceCriteria.marketPlacePolicy.backgroundPolicy}
                        onUpdatePolicy={(field, value) => updateMarketplacePolicyField('backgroundPolicy', field, value)}
                      />
                    </Box>
                  </AccordionDetails>
                )}
              </Accordion>

              <Box display="flex" justifyContent="center" mt={3} mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveMarketplaceCriteria}
                  disabled={savingCriteria}
                  style={{ minWidth: 200 }}
                >
                  {savingCriteria ? strings.MARKETPLACE.SAVING : strings.MARKETPLACE.SAVE_ALL}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        
        <Divider style={{ marginTop: 16, marginBottom: 16 }} />
        
        <FeatureToggleRow
          title={strings.MARKETPLACE.OPT_OUT_TITLE}
          description={strings.MARKETPLACE.OPT_OUT_DESCRIPTION}
          checked={featureFlags.optOutOfSharingDeclinedApplications}
          onChange={(checked) => handleFeatureFlagChange('optOutOfSharingDeclinedApplications', checked)}
          disabled={featureFlagsLoading}
        />
        
        <Alert severity="warning" style={{ marginTop: 24, marginBottom: 24 }}>
          {strings.MARKETPLACE.WARNING_MESSAGE}
        </Alert>
        </>
        )}
      </MuiCard>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelChange}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelChange} color="primary">
            {strings.MARKETPLACE.CANCEL}
          </Button>
          <Button 
            onClick={handleConfirmChange} 
            color="primary" 
            variant="contained"
            disabled={featureFlagsLoading}
          >
            {featureFlagsLoading ? strings.MARKETPLACE.UPDATING : strings.MARKETPLACE.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FeatureFlagsForm;